/**
 * Utilidades de fecha conscientes de zona horaria.
 * Las fechas de calendario se representan como strings `YYYY-MM-DD`.
 */

export const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Devuelve la fecha de calendario de `value` en la zona horaria indicada. */
export const dateKeyInTimeZone = (value: Date | string, timeZone: string): string => {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString('sv-SE', { timeZone });
};

/** Fecha de calendario de "hoy" en la zona horaria indicada. */
export const todayKeyInTimeZone = (timeZone: string): string =>
  dateKeyInTimeZone(new Date(), timeZone);

/** Suma (o resta) días a una clave `YYYY-MM-DD` sin tocar zonas horarias. */
export const addDaysToDateKey = (dateKey: string, days: number): string => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** Diferencia (en ms) entre la hora de pared en `timeZone` y UTC para un instante. */
const getTimeZoneOffsetMs = (instant: Date, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  const instantWithoutMs = Math.floor(instant.getTime() / 1000) * 1000;

  return asUtc - instantWithoutMs;
};

/** Instante UTC correspondiente al inicio o fin de un día local en `timeZone`. */
const zonedDayBoundary = (dateKey: string, timeZone: string, endOfDay: boolean): Date => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const wallClock = Date.UTC(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );

  let result = wallClock - getTimeZoneOffsetMs(new Date(wallClock), timeZone);

  // Recalcular en los cambios de horario de verano.
  const correctedOffset = getTimeZoneOffsetMs(new Date(result), timeZone);
  const corrected = wallClock - correctedOffset;
  if (corrected !== result) result = corrected;

  return new Date(result);
};

/**
 * Convierte un rango inclusivo de días de calendario (`YYYY-MM-DD`) en los
 * instantes UTC que cubren esos días completos en la zona horaria indicada.
 */
export const dayRangeToUtc = (
  startDateKey: string,
  endDateKey: string,
  timeZone: string,
): { startISO: string; endISO: string } => ({
  startISO: zonedDayBoundary(startDateKey, timeZone, false).toISOString(),
  endISO: zonedDayBoundary(endDateKey, timeZone, true).toISOString(),
});

/** Clave `YYYY-MM-DD` del primer día del mes de `dateKey`. */
export const monthStartKey = (dateKey: string): string =>
  `${dateKey.slice(0, 7)}-01`;
